import React from 'react'

import type { DocumentMeta } from '@/persistence/local'
import { createLogger } from '@/debug/logger'
import { DEFAULT_THUMBNAIL_HEIGHT, DEFAULT_THUMBNAIL_WIDTH } from '@/capture/thumbnailRenderConfig'
import { ensureThumbnail, getCachedThumbnail, isThumbnailStaleForSize, loadCachedThumbnail } from '@/persistence/local-thumbnails'
import { useLazyThumbnail } from '@/components/useLazyThumbnail'
import { LoaderCircle } from 'lucide-react'

export type DocumentThumbnailProps = {
  id: string
  width?: number
  height?: number
  className?: string
  style?: React.CSSProperties
  alt?: string
  meta?: DocumentMeta
  showCommitBadge?: boolean
}

export function DocumentThumbnail({ id, width = DEFAULT_THUMBNAIL_WIDTH, height = DEFAULT_THUMBNAIL_HEIGHT, className, style, alt = 'thumbnail', meta, showCommitBadge = true }: DocumentThumbnailProps) {
  const log = React.useMemo(() => createLogger('thumbnail-ui'), [])
  const getCached = React.useCallback(() => getCachedThumbnail(id), [id])
  const loadCached = React.useCallback(() => loadCachedThumbnail(id), [id])
  const ensureDocumentThumbnail = React.useCallback(() => {
    return ensureThumbnail(id, { width, height, commitId: meta?.commitId })
  }, [height, id, meta?.commitId, width])
  const handleThumbnailError = React.useCallback((err: unknown) => {
    log.error('thumbnail load failed', { id, err })
  }, [id, log])
  const { src, setSrc, wrapperRef } = useLazyThumbnail({
    cacheKey: id,
    getCached,
    loadCached,
    ensure: ensureDocumentThumbnail,
    onError: handleThumbnailError,
  })

  // Check staleness and refresh if needed
  React.useEffect(() => {
    if (!meta) return
    let cancelled = false
    const stale = isThumbnailStaleForSize(meta, { width, height })
    if (stale) {
      log.debug('stale thumbnail detected, refreshing', { id, width, height })
      ensureDocumentThumbnail().then((url) => {
        if (!cancelled && url) setSrc(url)
      }).catch((err) => {
        log.error('failed to refresh stale thumbnail', { id, err })
      })
    }
    return () => {
      cancelled = true
    }
  }, [ensureDocumentThumbnail, height, id, log, meta, setSrc, width])

  return (
    <div ref={wrapperRef} className={className} style={{ width: '100%', height: '100%', ...style }}>
      {src ? (
        <div className="relative w-full h-full">
          <img src={src} alt={alt} className="w-full h-full object-cover block" />
          {showCommitBadge && typeof meta?.commitId === 'number' && (
            <div className="absolute bottom-1 right-1 text-[10px] leading-none px-1.5 py-1 rounded bg-black/60 text-white/80">
              c{String(meta.commitId)}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full bg-white/10 flex items-center justify-center">
          <LoaderCircle className="text-gray-400 animate-spin" size={20} />
        </div>
      )}
    </div>
  )
}
